import React, { useEffect, useRef, useState } from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  decideFlttMatchProposal,
  listFlttMatchProposals,
} from "../funtionsBD/Mainservice";
import "./FlttMatchProposalDialog.css";

function errorMessage(error, fallback) {
  return error?.response?.data?.message || error?.message || fallback;
}

export function useFlttMatchReview(clubId, enabled, onChanged) {
  const [proposals, setProposals] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const snoozed = useRef(false);
  const onChangedRef = useRef(onChanged);
  onChangedRef.current = onChanged;

  const refresh = async ({ forceOpen = false } = {}) => {
    if (!clubId) return [];
    try {
      const response = await listFlttMatchProposals(clubId);
      const rows = response.data?.data?.proposals || [];
      setProposals(rows);
      if (rows.length && (forceOpen || !snoozed.current)) setOpen(true);
      if (!rows.length) setOpen(false);
      return rows;
    } catch (err) {
      console.error(err);
      return [];
    }
  };

  useEffect(() => {
    if (!enabled || !clubId) {
      setOpen(false);
      return undefined;
    }
    snoozed.current = false;
    refresh();
    return undefined;
  }, [enabled, clubId]);

  useEffect(() => {
    if (proposals.length === 0) setOpen(false);
  }, [proposals.length]);

  const present = (incoming) => {
    const rows = Array.isArray(incoming) ? incoming : [];
    if (!rows.length) return;
    setProposals((prev) => {
      const byId = new Map(prev.map((row) => [row.proposal_id, row]));
      rows.forEach((row) => {
        if (row?.proposal_id) byId.set(row.proposal_id, row);
      });
      return [...byId.values()];
    });
    snoozed.current = false;
    setOpen(true);
  };

  const decide = async (proposalId, decision) => {
    if (!clubId || !proposalId || busy) return;
    setBusy(true);
    setError("");
    try {
      await decideFlttMatchProposal(clubId, proposalId, decision);
      setProposals((prev) => prev.filter((row) => row.proposal_id !== proposalId));
      onChangedRef.current?.();
    } catch (err) {
      setError(errorMessage(err, "Could not save this decision"));
    } finally {
      setBusy(false);
    }
  };

  const later = () => {
    snoozed.current = true;
    setOpen(false);
    setError("");
  };

  return {
    proposals,
    open,
    busy,
    error,
    setOpen,
    present,
    refresh,
    accept: (proposalId) => decide(proposalId, "accept"),
    reject: (proposalId) => decide(proposalId, "reject"),
    later,
  };
}

export default function FlttMatchProposalDialog({
  open,
  proposals,
  busy,
  error,
  onAccept,
  onReject,
  onLater,
}) {
  const { t } = useTranslation();
  const current = proposals?.[0];
  if (!current) return null;
  const remaining = proposals.length;

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onLater}
      maxWidth="sm"
      fullWidth
      sx={{ zIndex: 2000 }}
    >
      <DialogTitle>
        {t("flttMatchTitle", { defaultValue: "Possible same player" })}
      </DialogTitle>
      <DialogContent>
        {remaining > 1 ? (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {t("flttMatchProgress", {
              current: 1,
              total: remaining,
              defaultValue: "{{current}} of {{total}}",
            })}
          </Typography>
        ) : null}
        <Typography>
          {t("flttMatchBody", {
            incoming: current.incoming_name,
            existing: current.existing_name,
            defaultValue:
              "FLTT added {{incoming}}. This club already has {{existing}}. Are they the same player?",
          })}
        </Typography>
        {error ? (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        ) : null}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Button onClick={onLater} disabled={busy} sx={{ textTransform: "none" }}>
          {t("flttMatchLater", { defaultValue: "Not now" })}
        </Button>
        <Button
          onClick={() => onReject(current.proposal_id)}
          disabled={busy}
          color="inherit"
          sx={{ textTransform: "none" }}
        >
          {t("flttMatchSeparate", { defaultValue: "Keep separate" })}
        </Button>
        <Button
          onClick={() => onAccept(current.proposal_id)}
          disabled={busy}
          variant="contained"
          sx={{ textTransform: "none" }}
        >
          {t("flttMatchConfirm", { defaultValue: "Match" })}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
